export class WeCollectionScrollPosition {
  constructor () {
    this.storageKey = 'weCollectionScrollPosition'
    this.currentUrl = this.urlToPathName(window.location.href)
    this.isCollectionPage = this.currentUrl.includes('/collections/')
    this.isProductPage = this.currentUrl.includes('/products/')

    this.init()
  }

  init () {
    if (this.isCollectionPage) {
      this.handleCollectionPage()
    } else if (this.isProductPage) {
      // On product page, we don't need to do anything
      // The scroll position was already saved when the product was clicked
    }
  }

  handleCollectionPage () {
    // Disable browser's native scrollRestoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    // Check if we're coming back via browser back button from a product page
    const savedData = this.getSavedData()

    if (savedData && savedData.collectionUrl === this.currentUrl) {
      // Check if we should restore based on timestamp
      // If data was saved more than 1 second ago, it means we navigated away
      const timeSinceSave = Date.now() - (savedData.timestamp || 0)
      const shouldRestore = timeSinceSave > 1000 // More than 1 second ago

      if (shouldRestore) {
        // Wait a bit for DOM to be ready
        setTimeout(() => {
          this.restoreScrollPosition(savedData.productHandle)
        }, 100)
      }
    }

    // Listen for popstate events (browser back/forward button)
    this.setupPopstateListener()

    // Capture clicks on product cards in collection pages
    this.captureProductClicks()
  }

  setupPopstateListener () {
    window.addEventListener('popstate', () => {
      // Re-check if we're on a collection page after navigation
      const currentPath = this.urlToPathName(window.location.href)

      if (currentPath.includes('/collections/')) {
        const savedData = this.getSavedData()

        if (savedData && savedData.collectionUrl === currentPath) {
          // Wait a bit for DOM to update (collection filters might be loading)
          setTimeout(() => {
            this.restoreScrollPosition(savedData.productHandle)
          }, 200)
        }
      }
    })
  }

  captureProductClicks () {
    // Use capture phase to catch clicks early
    document.addEventListener(
      'click',
      (e) => {
        // Only handle clicks on collection pages (check dynamically)
        const currentPath = this.urlToPathName(window.location.href)
        if (!currentPath.includes('/collections/')) return

        // Find the product card element (article with data-product-handle)
        const productCard = e.target.closest('[data-product-handle]')
        if (!productCard) return

        // Get product handle directly from the data attribute (this ensures exact match)
        const productHandle = productCard.getAttribute('data-product-handle')
        if (!productHandle) return

        // Save the product handle, collection URL, and timestamp
        this.saveData({
          collectionUrl: currentPath,
          productHandle: productHandle,
          timestamp: Date.now(),
        })
      },
      true // Use capture phase
    )
  }

  restoreScrollPosition (productHandle) {
    if (!productHandle) return

    // Wait for DOM to be ready and products to be rendered
    const checkForProduct = () => {
      // Try to find the product by data-product-handle attribute
      const productCard = document.querySelector(
        `[data-product-handle="${productHandle}"]`
      )

      if (productCard) {
        // Product found, scroll to it
        productCard.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        })
        // Clear the saved data after successful restoration
        this.clearData()
      }
    }

    // Check immediately
    checkForProduct()

    // Also check after a short delay in case products are still loading
    setTimeout(checkForProduct, 100)
  }

  saveData (data) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(data))
    } catch (e) {
      console.warn('Failed to save scroll position:', e)
    }
  }

  getSavedData () {
    try {
      const data = sessionStorage.getItem(this.storageKey)
      return data ? JSON.parse(data) : null
    } catch (e) {
      console.warn('Failed to get scroll position:', e)
      return null
    }
  }

  clearData () {
    try {
      sessionStorage.removeItem(this.storageKey)
    } catch (e) {
      console.warn('Failed to clear scroll position:', e)
    }
  }


  extractProductHandle (href) {
    if (!href) return null

    // Extract handle from URLs like:
    // /products/product-handle
    // /products/product-handle?variant=123
    const match = href.match(/\/products\/([^/?]+)/)
    return match ? match[1] : null
  }

  urlToPathName (url) {
    try {
      const object = new URL(url)
      return object.pathname
    } catch (e) {
      return url.split('?')[0]
    }
  }
}

// Initialize immediately and also on DOMContentLoaded
// This handles both full page loads and AJAX-loaded content
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // eslint-disable-next-line no-new
    new WeCollectionScrollPosition()
  })
} else {
  // DOM already loaded
  // eslint-disable-next-line no-new
  new WeCollectionScrollPosition()
}
